import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  People as PeopleIcon,
  Male as MaleIcon,
  Female as FemaleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { Team } from '../services/api';

interface TeamCardProps {
  team: Team;
  onEdit?: (team: Team) => void;
  onDelete?: (teamId: string) => void;
  showActions?: boolean;
}

const TeamCard: React.FC<TeamCardProps> = ({ team, onEdit, onDelete, showActions = false }) => {
  const getDoublesTypeLabel = (type: string) => {
    switch (type) {
      case 'men':
        return "Men's Doubles";
      case 'women':
        return "Women's Doubles";
      case 'mixed':
        return 'Mixed Doubles';
      default:
        return type;
    }
  };

  const getDoublesTypeColor = (type: string): 'primary' | 'secondary' | 'success' => {
    switch (type) {
      case 'men':
        return 'primary';
      case 'women':
        return 'secondary';
      case 'mixed':
        return 'success';
      default:
        return 'primary';
    }
  };

  const getGenderIcon = (gender: string) => {
    return gender === 'male' ? <MaleIcon fontSize="small" /> : <FemaleIcon fontSize="small" />;
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon color="action" />
            <Typography variant="h6" component="div">
              {team.team_name}
            </Typography>
          </Box>
          {showActions && (
            <Box>
              {onEdit && (
                <Tooltip title="Edit team name">
                  <IconButton size="small" onClick={() => onEdit(team)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {onDelete && (
                <Tooltip title="Delete team">
                  <IconButton size="small" color="error" onClick={() => onDelete(team.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>

        <Chip
          label={getDoublesTypeLabel(team.doubles_type)}
          color={getDoublesTypeColor(team.doubles_type)}
          size="small"
          sx={{ mb: 2 }}
        />

        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Team Members
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getGenderIcon(team.player1.gender)}
              <Typography variant="body2">
                {team.player1.name}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {getGenderIcon(team.player2.gender)}
              <Typography variant="body2">
                {team.player2.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Registered: {new Date(team.created_at).toLocaleDateString()}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default TeamCard;
